import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ThemeSwitcher } from "@/components/ThemeSwitcher";
import { LanguageSwitcher } from "@/components/i18n/LanguageSwitcher";
import { useAuth } from "@/hooks/useAuth";
import { Shell } from "@/components/layout/Shell";
import { useTranslation } from "react-i18next";
import { TranslationButton } from "@/components/i18n/TranslationButton";
import { TranslationText } from "@/components/i18n/TranslationText";
import { Check, BarChart3, FileText, FileCheck, Clock } from "lucide-react";
import { HeroSection } from "@/components/landing/HeroSection";
import { FeaturesSection } from "@/components/landing/FeaturesSection";
import { TestimonialsSection } from "@/components/landing/TestimonialsSection";
import { PricingSection } from "@/components/landing/PricingSection";
import { CTASection } from "@/components/landing/CTASection";
import { Footer } from "@/components/landing/Footer";
import { Helmet } from "react-helmet";

export default function IndexPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800">
      <Helmet>
        <title>Subtrackr – Contract Management Made Easy</title>
        <meta name="description" content="Subtrackr helps you take control of contracts with reminders, analytics, and powerful management tools." />
        {/* Confirmed: No "Lovable Generated Project" here. */}
      </Helmet>
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-primary/5 rounded-full blur-3xl transform translate-x-1/3 -translate-y-1/3"></div>
      <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-blue-400/5 rounded-full blur-3xl transform -translate-x-1/3 translate-y-1/3"></div>
      
      {/* Header */}
      <header className="py-4 px-6 backdrop-blur-lg bg-white/80 dark:bg-gray-900/80 border-b border-gray-200 dark:border-gray-800 fixed top-0 left-0 right-0 z-40 transition-all duration-300">
        <div className="container flex justify-between items-center">
          <div className="flex items-center gap-2">
            <a href="#" className="transition-transform hover:scale-105">
              <img
                src="/lovable-uploads/b39387ca-08fc-4e1c-a0f7-79c2fa0f4845.png"
                alt="Subtrackr Logo"
                className="h-8"
              />
            </a>
          </div>
          <div className="flex items-center gap-4">
            {/* <LanguageSwitcher /> - Hidden for now */}
            <ThemeSwitcher />
            {isAuthenticated ? (
              <TranslationButton 
                i18nKey="goToDashboard" 
                onClick={() => navigate("/dashboard")} 
                className="bg-primary hover:bg-primary/90 transition-all duration-200"
              />
            ) : (
              <div className="flex items-center gap-2">
                <TranslationButton 
                  i18nKey="login" 
                  onClick={() => navigate("/login")} 
                  variant="ghost" 
                  className="hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200"
                />
                <TranslationButton 
                  i18nKey="signup" 
                  onClick={() => navigate("/register")} 
                  className="bg-primary hover:bg-primary/90 transition-all duration-200"
                />
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 pt-24 relative z-10">
        {/* Hero Section */}
        <HeroSection isAuthenticated={isAuthenticated} navigate={navigate} />
        
        {/* Features Section */}
        <FeaturesSection />
        
        {/* Testimonials Section */}
        <TestimonialsSection />
        
        {/* Pricing Section */}
        <PricingSection />
        
        {/* CTA Section */}
        <CTASection isAuthenticated={isAuthenticated} navigate={navigate} />
      </main>

      <Footer />
    </div>
  );
}
